import config from "../../../../config/backend";
import type {
  StudentRepository,
  StudentSummaryVM,
  SignAttendanceSheetBody,
  SignAttendanceSheetResponse,
  SignAllAttendanceSheetsBody,
  SignAllAttendanceSheetsResponse,
  AttendanceSheetVM,
  StudentPendingActionsVM,
  PendingSurveyVM,
  PendingObjectiveAssignmentGroupVM,
  TrainingItemVM,
  TrainingItemDetailsVM,
  ManageObjectivesCommand,
  SubmitSurveyCommand,
  CreateExternalProviderBatchRequestBody,
  StudentVM,
  UpdateStudentBody,
  TeacherVM,
  RefundRequestBody,
  FundingRequestEligibilityVM,
  CreateFundingRequestBody,
  StudentFundingRequestVM,
  GenerateCoverLetterBody,
  GenerateCoverLetterResponse,
} from "@shared-kernel/application/ports/student/student-repository";
import type { EligibleTrainingItemsVM } from "@academy-context/shared/domain/types/eligible-training-items";
import type { TRAINING_ITEM_TYPE } from "@academy-context/shared/domain/types/training-items";
import type { TokenStorageGateway } from "@shared-kernel/application/ports/token-storage-gateway";
import { HttpGateway } from "@shared-kernel/secondary/http-gateway";

export class BackendStudentRepository extends HttpGateway implements StudentRepository {
  private _route: string = "v1/student";
  private _refundRequestRoute: string = "v1/student/refund-requests";

  constructor(tokenStorageGateway: TokenStorageGateway) {
    super(config.url, tokenStorageGateway);
  }

  async getAllAttendanceSheets(): Promise<AttendanceSheetVM[]> {
    const { data: attendanceSheets } = await this._instance.get(`${this._route}/attendance-sheets`);
    return attendanceSheets;
  }

  async getUnsignedAttendanceSheets(): Promise<AttendanceSheetVM[]> {
    const { data: unsignedAttendanceSheets } = await this._instance.get(`${this._route}/unsigned-attendance-sheets`);
    return unsignedAttendanceSheets;
  }

  async signAttendanceSheet(body: SignAttendanceSheetBody): Promise<SignAttendanceSheetResponse> {
    const { attendanceSheetId, type, signature } = body;
    const formDataBody = new FormData();
    formDataBody.append("signature", signature);

    const { data } = await this._instance.patch(`${this._route}/attendance-sheets/${attendanceSheetId}:sign`, formDataBody, {
      params: { type },
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return data;
  }

  async signAllAttendanceSheets(body: SignAllAttendanceSheetsBody): Promise<SignAllAttendanceSheetsResponse> {
    const { signature } = body;
    const formDataBody = new FormData();
    formDataBody.append("signature", signature);

    const { data } = await this._instance.patch(`${this._route}/attendance-sheets:sign-all`, formDataBody, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return data;
  }

  async hasPendingActions(): Promise<StudentPendingActionsVM> {
    const { data: pendingActions } = await this._instance.get(`${this._route}/has-pending-actions`);
    return pendingActions;
  }

  async getPendingSurveys(): Promise<PendingSurveyVM[]> {
    const { data: pendingSurveys } = await this._instance.get(`${this._route}/pending-surveys`);
    return pendingSurveys;
  }

  async getPendingObjectiveAssignments(): Promise<PendingObjectiveAssignmentGroupVM[]> {
    const { data: pendingObjectives } = await this._instance.get(`${this._route}/pending-objective-assignments`);
    return pendingObjectives;
  }

  async getAllTrainingItems(): Promise<TrainingItemVM[]> {
    const { data: trainingItems } = await this._instance.get(`${this._route}/training-items`);
    return trainingItems;
  }

  async getTrainingItemById(id: string, type: TRAINING_ITEM_TYPE): Promise<TrainingItemDetailsVM> {
    const { data: trainingItemDetails } = await this._instance.get(`${this._route}/training-items/${id}`, {
      params: { type },
    });
    return trainingItemDetails;
  }

  async manageObjectives(command: ManageObjectivesCommand): Promise<void> {
    await this._instance.post(`${this._route}/objectives:assign`, command);
  }

  async submitSurvey(command: SubmitSurveyCommand): Promise<void> {
    await this._instance.post(`${this._route}/surveys:submit`, command);
  }

  async createExternalProviderBatchRequest(body: CreateExternalProviderBatchRequestBody): Promise<string> {
    const { data: requestId } = await this._instance.post(`${this._route}/external-provider-batch-requests`, body);
    return requestId;
  }

  // Profile methods (merged from BackendStudentGateway)
  async profile(): Promise<StudentVM> {
    const { data: student } = await this._instance.get(`${this._route}/profile`);
    return student;
  }

  async update(body: UpdateStudentBody): Promise<void> {
    await this._instance.patch(`${this._route}/profile`, body);
  }

  async summary(): Promise<StudentSummaryVM> {
    const { data: summary } = await this._instance.get(`${this._route}/summary`);
    return summary;
  }

  async teacherList(): Promise<TeacherVM[]> {
    const { data: teachers } = await this._instance.get(`${this._route}/teachers`);
    return teachers;
  }

  async favoriteTeacherList(): Promise<TeacherVM[]> {
    const { data: teachers } = await this._instance.get(`${this._route}/favorite-teachers`);
    return teachers;
  }

  // Refund Requests
  async requestRefund(refund: RefundRequestBody): Promise<void> {
    await this._instance.post(this._refundRequestRoute, refund);
  }

  async uploadInvoice(file: File): Promise<string> {
    const formDataBody = new FormData();
    formDataBody.append("invoice", file);
    const res = await this._instance.post(`${this._refundRequestRoute}/invoices`, formDataBody);
    return res.data;
  }

  // Funding Requests
  async createFundingRequest(fundingRequest: CreateFundingRequestBody): Promise<string> {
    const { data: fundingRequestId } = await this._instance.post(this._route + "/funding-requests", fundingRequest);
    return fundingRequestId;
  }

  async fundingRequestEligibility(): Promise<FundingRequestEligibilityVM> {
    const { data: fundingRequestEligibility } = await this._instance.get(`${this._route}/funding-requests/eligibility`);
    return fundingRequestEligibility;
  }

  async getAllFundingRequests(): Promise<StudentFundingRequestVM[]> {
    const { data: fundingRequests } = await this._instance.get(`${this._route}/funding-requests`);
    return fundingRequests;
  }

  async getEligibleTrainingItems(): Promise<EligibleTrainingItemsVM> {
    const { data } = await this._instance.get(`${this._route}/funding-request-eligible-training-items`);
    return data;
  }

  async generateCoverLetter(body: GenerateCoverLetterBody): Promise<GenerateCoverLetterResponse> {
    const { data } = await this._instance.post(`${this._route}/text-generation/cover-letter`, body);
    return data;
  }
}
